/**
 * UnifiedWalletDropdown
 *
 * Single wallet button for dual users that opens a small menu with two
 * choices — Wallet (client side) and Earnings (filmmaker side). Each choice
 * opens its existing dialog. Mirrors the mobile Wallet/Earnings toggle.
 */
import React, { useState } from 'react';
import { Wallet, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useClientWallet } from '@/hooks/use-client-wallet';
import { usePayments } from '@/hooks/use-payments';
import { useStripeOnboarding } from '@/hooks/use-stripe-onboarding';
import { WalletDialog } from '@/components/wallet/WalletDialog';
import { FilmmakerWalletDialog } from '@/components/wallet/FilmmakerWalletDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const UnifiedWalletDropdown: React.FC = () => {
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [isEarningsOpen, setIsEarningsOpen] = useState(false);

  const { data: walletData } = useClientWallet();
  const walletBalance = walletData?.wallet ?? 0;
  const hasClientAccount = !!walletData?.client_id;

  const { totalEarnings, isLoading: isEarningsLoading } = usePayments();
  const { handleAddPaymentMethodWithStripe, isCreatingOnboardingLink } = useStripeOnboarding();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-muted transition-colors group text-sm font-medium text-muted-foreground hover:text-foreground"
            title="Wallet & Earnings"
          >
            <Wallet className="h-4 w-4" />
            <span>£{walletBalance.toLocaleString()}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {hasClientAccount && (
            <DropdownMenuItem onClick={() => setIsWalletOpen(true)} className="cursor-pointer">
              <ArrowDownLeft className="h-4 w-4 mr-2 text-blue-600" />
              <div className="flex-1">
                <div className="text-sm font-medium">Wallet</div>
                <div className="text-xs text-muted-foreground">
                  £{walletBalance.toLocaleString()} available
                </div>
              </div>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setIsEarningsOpen(true)} className="cursor-pointer">
            <ArrowUpRight className="h-4 w-4 mr-2 text-green-600" />
            <div className="flex-1">
              <div className="text-sm font-medium">Earnings</div>
              <div className="text-xs text-muted-foreground">
                {isEarningsLoading ? 'Loading…' : totalEarnings}
              </div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <WalletDialog
        open={isWalletOpen}
        onOpenChange={setIsWalletOpen}
        clientId={walletData?.client_id}
        walletBalance={walletBalance}
      />
      <FilmmakerWalletDialog
        open={isEarningsOpen}
        onOpenChange={setIsEarningsOpen}
        onAddBank={handleAddPaymentMethodWithStripe}
        isCreatingOnboardingLink={isCreatingOnboardingLink}
      />
    </>
  );
};
